import type { Field, Item, Order, Payout } from 'models';
import { fetch } from 'services/apiV3/fetch';
import type { SupportedLocalesKeys } from 'utils';
import type { FetchResponse } from 'utils/fetchJson/fetchJson';
import { fetchJson } from 'utils/fetchJson/fetchJson';
import { API_URL_V3 } from '../../../constants';

type EntityFields = { fields: Field[]; id?: string; provider?: string };
export type OfferEntityFields = EntityFields & { id: string };
export type RecipientEntityFields = EntityFields & {
  items: Pick<Item, 'id' | 'amount'>[];
};

export type CreatePayload = {
  callback: {
    id: string;
    url?: string;
    version?: string;
  };
  fingerprint?: string | null;
  locale: SupportedLocalesKeys;
  metadata?: {
    tracking_url?: string;
    payor_id?: string;
  };
  offer: OfferEntityFields;
  payables: Payout[];
  provider: string;
  recipient: RecipientEntityFields;
  return_url: string;
  mute_email_notifications?: boolean;
  sender: EntityFields;
};

export type UpdatePayload = {
  callback: {
    id: string;
    url?: string;
    version?: string;
  };
  fingerprint?: string | null;
  locale: SupportedLocalesKeys;
  metadata?: {
    tracking_url: string;
  };
  offer: OfferEntityFields;
  orderId: string;
  payables: Payout[];
  provider: string;
  recipient: RecipientEntityFields;
  return_url: string;
  sender: EntityFields;
  token: string;
};

export type OrderServiceError = {
  message?: string;
  param?: string;
  source?: string;
  type?: string;
};

type OrderService = {
  create: (payload: CreatePayload) => Promise<Order>;
  update: (payload: UpdatePayload) => Promise<any>;
  fetch: (payload: any) => Promise<FetchResponse>;
  createPayment: (payload: any) => Promise<any>;
  fetchPayment: (payload: any) => Promise<any>;
  cancelPayment: (payload: any) => Promise<any>;
  initiatePayment: (payload: any) => Promise<any>;
  addOrderToUser: (payload: any) => Promise<any>;
  ownership: (payload: any) => Promise<any>;
  addCoupon: (payload: any) => Promise<any>;
};

const orders: OrderService = {
  create: ({ locale = 'EN', ...payload }) => {
    const path = `${API_URL_V3}/orders?locale=${locale}`;
    const body = JSON.stringify(payload);

    const options = { method: 'POST', body };

    return fetchJson(path, options);
  },

  update: ({ orderId, token, ...payload }) => {
    const path = `${API_URL_V3}/orders/${orderId}?token=${token}`;
    const body = JSON.stringify(payload);

    const options = { method: 'PATCH', body };

    return fetch(path, options);
  },

  fetch: ({ orderId, token }): Promise<FetchResponse> => {
    const path = `${API_URL_V3}/orders/${orderId}?token=${token}`;

    return fetch(path, { method: 'GET' });
  },

  createPayment: ({
    orderId,
    token,
    locale = 'EN',
    hideUserManagement = false,
  }) => {
    let path = `${API_URL_V3}/orders/${orderId}/payments?token=${token}&locale=${locale}`;
    if (hideUserManagement) {
      path += '&hideUserManagement=true';
    }

    const options = { method: 'POST' };

    return fetchJson(path, options);
  },

  fetchPayment: ({ orderId, token, locale = 'EN' }) => {
    const path = `${API_URL_V3}/orders/${orderId}/payments?token=${token}&locale=${locale}`;
    return fetch(path, { method: 'GET' });
  },

  cancelPayment: ({ url, method, cancellationReason }) => {
    const body = {};
    if (cancellationReason) {
      body.cancellation_reason = cancellationReason;
    }

    const options = {
      method,
      body: JSON.stringify(body),
    };

    return fetch(url, options);
  },

  initiatePayment: ({ url, method, date }) => {
    const body = JSON.stringify({ date });
    const options = {
      method,
      body,
    };

    return fetch(url, options);
  },

  addOrderToUser: ({ orderId, token }) => {
    const path = `${API_URL_V3}/orders/${orderId}/ownership?token=${token}`;
    const method = 'POST';

    return fetch(path, { method });
  },

  ownership: ({ id, token }) => {
    const path = `${API_URL_V3}/orders/${id}/ownership?token=${token}`;

    return fetch(path, { method: 'GET' });
  },

  addCoupon: ({ id, token, couponCode }) => {
    const path = `${API_URL_V3}/orders/${id}/coupon?token=${token}`;
    const body = JSON.stringify({ coupon_code: couponCode });
    const options = { method: 'PATCH', body };

    return fetchJson(path, options);
  },
};

export { orders };
